import { isEmpty, isNil } from 'ramda';
import moment from 'moment';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { getCookie } from '../utils/cookie';
import { buildReportQuery } from '../utils/buildReportQuery';

import request, {
  apiURL,
  requestToDownload,
  get,
  post,
  getDownload,
  postDownload,
} from '../utils/api';
const reportingServiceAPIURL = import.meta.env
  .REACT_APP_REPORTING_SERVICE_API_URL;
export default class ReportService {
  // Unified function for both legacy and new reports
  createCentralizedReport(params, options) {
    const version = params.version || 'legacy';

    if (version === 'new') {
      // New report (custom reports)
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const body = {
        organizationId: params.organizationId,
        reportId: params.reportId,
        version: 'new',
        timezone: timeZone,
      };

      // Add optional fields
      if (params.fileType) {
        body.fileType = params.fileType;
      }
      if (params.properties && params.properties.length > 0) {
        body.properties = params.properties;
      }
      if (params.exportAs) {
        body.exportAs = params.exportAs;
      }
      if (params.timeSelection) {
        body.timeSelection = params.timeSelection.toUpperCase();
      }
      // A YEAR report carries only the compliance year. Read it off the picker's
      // own value rather than formatting the date, so a start-of-year value is
      // never pulled back into the previous year by a timezone conversion.
      if (params.year) {
        body.year =
          typeof params.year?.format === 'function'
            ? params.year.format('YYYY')
            : String(params.year).slice(0, 4);
      }
      if (params.date) {
        body.date = formatDateDB(params.date);
      }
      if (params.year) {
        body.year = moment(params.year).format('YYYY');
      }
      if (params.dateFrom) {
        body.dateFrom = formatDateDB(params.dateFrom);
      }
      if (params.dateTo) {
        body.dateTo = formatDateDB(params.dateTo);
      }

      return post(
        `${reportingServiceAPIURL}/centralized-reports/generate`,
        JSON.stringify(body),
        {
          ...options,
          isOtherApi: true,
        },
      );
    }

    // Legacy report (date range / year based)
    const {
      dateRangeStart,
      dateRangeEnd,
      year,
      organizationId,
      userId: requestedBy,
      username: requestedByUsername,
      fortressReportId,
      variant,
      dateRangeType,
      projectName,
      fileType,
    } = params;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const variantSpecificOptions = {
      ...(variant === 'year' && { year }),
      ...(variant === 'date-range' && {
        dateRangeStart: formatDateDB(dateRangeStart),
        dateRangeEnd: formatDateDB(dateRangeEnd),
        dateRangeType,
      }),
    };

    return post(
      `${reportingServiceAPIURL}/centralized-reports/generate`,
      JSON.stringify({
        ...variantSpecificOptions,
        requestedBy,
        requestedByUsername,
        fortressReportId,
        organizationId,
        version: 'legacy',
        projectName,
        format: fileType,
        timezone: timeZone,
      }),
      { ...options, isOtherApi: true },
    );
  }
  getCentralizedReports({ organizationId, options }) {
    return get(`/${organizationId}/reports`, options);
  }
  getEligibleReportProperties({
    organizationId,
    userId,
    reportReferenceId,
    options,
  }) {
    return get(
      `/${organizationId}/users/${userId}/properties?reportReferenceId=${reportReferenceId}`,
      options,
    );
  }
  getCentralizedReportDefinitions({ organizationId, all, options }) {
    const queryParams = all ? '?all=true' : '';
    return get(
      `${reportingServiceAPIURL}/centralized-reports/${organizationId}${queryParams}`,
      {
        ...options,
        isOtherApi: true,
      },
    );
  }
  downloadCustomReport({
    organizationId,
    fileType,
    payload,
    filename,
    options,
  }) {
    // Note: This override is only for local testing against the reporting lambda.
    // In all other environments it falls back to the standard backend API.
    const lambdaBaseUrl = import.meta.env
      .REACT_APP_REPORTING_SERVICE_LAMBDA_API_URL;
    const baseUrl = lambdaBaseUrl || '';
    const isOtherApi = Boolean(lambdaBaseUrl);
    const url = isOtherApi
      ? `${baseUrl}/${organizationId}/centralized-report?fileType=${fileType}`
      : `/${organizationId}/centralized-report?fileType=${fileType}`;

    return postDownload(url, filename, JSON.stringify(payload), {
      ...options,
      ...(isOtherApi ? { isOtherApi } : {}),
    });
  }
  getCentralizedReportStatus({ organizationId, options }) {
    return get(`${reportingServiceAPIURL}/reports/${organizationId}/latest`, {
      ...options,
      isOtherApi: true,
    });
  }
  downloadCentralizedReport(
    organizationId: string,
    reportId: string,
    filename: string,
    version?: string,
    executionId?: string,
  ) {
    const params = new URLSearchParams();
    if (version) params.append('version', version);
    if (executionId) params.append('executionId', executionId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return getDownload(
      `/${organizationId}/reports/${reportId}${queryString}`,
      filename,
    );
  }
  getCentralizedHistoricalReports({ organizationId, reportId, options }) {
    return get(`/${organizationId}/reports/historical/${reportId}`, options);
  }
  getAllReports(
    organizationId: string,
    propertyId: string,
    sorting?: any,
    searchText: ?string,
    optionArgs?: Object,
  ) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    const options = { ...defaultOptions, ...optionArgs };
    const sort =
      sorting && sorting.fieldName === ''
        ? ''
        : // $FlowFixMe
          `sort=${sorting.fieldName}&order=${sorting.order}`;
    const query =
      isNil(searchText) || searchText === '' ? '' : `&query=${searchText}`;
    const queryString = `?${sort}${query}`;

    return request(
      `${apiURL}/${organizationId}/${propertyId}/reports${queryString}`,
      options,
    );
  }
  getHistoricalReports({
    organizationId,
    propertyId,
    dateTo,
    dateFrom,
    reportId,
  }: Object) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    const filterDates = `dateTo=${dateTo}&dateFrom=${dateFrom}`;
    const queryString = `?${filterDates}`;
    return request(
      `${apiURL}/${organizationId}/${propertyId}/reports/historical/${reportId}${queryString}`,
      options,
    );
  }
  getProjectNames({ organizationId, options }) {
    return get(`/report/${organizationId}/project-names`, options);
  }

  downloadXLSMReport(
    organizationId: string,
    propertyId: string,
    reportId: string,
    startDate: string,
    endDate: string,
    period: string,
    date: string,
    startPeriod: string,
    endPeriod: string,
    scheduledPeriod: string,
    actualPeriod: string,
    currentPeriod: string,
    futurePeriod: string,
    customDateField: string,
    leaseType: string,
    query = '',
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    // Use util to build query string
    const finalQuery = buildReportQuery({
      period,
      startDate,
      endDate,
      startPeriod,
      endPeriod,
      scheduledPeriod,
      actualPeriod,
      currentPeriod,
      futurePeriod,
      date,
      customDateField,
      leaseType,
      query,
    });
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/xlsm-reports/${reportId}${finalQuery}`,
      options,
    );
  }

  downloadXLSXReport(
    organizationId: string,
    propertyId: string,
    reportId: string,
    startDate: string,
    endDate: string,
    period: string,
    date: string,
    startPeriod: string,
    endPeriod: string,
    scheduledPeriod: string,
    actualPeriod: string,
    currentPeriod: string,
    futurePeriod: string,
    customDateField: string,
    leaseType: string,
    query = '',
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    // Use util to build query string
    const finalQuery = buildReportQuery({
      period,
      startDate,
      endDate,
      startPeriod,
      endPeriod,
      scheduledPeriod,
      actualPeriod,
      currentPeriod,
      futurePeriod,
      date,
      customDateField,
      leaseType,
      query,
    });
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/xlsx-reports/${reportId}${finalQuery}`,
      options,
    );
  }

  downloadCSVReport(
    organizationId: string,
    propertyId: string,
    reportId: string,
    startDate: string,
    endDate: string,
    period: string,
    date: string,
    startPeriod: string,
    endPeriod: string,
    scheduledPeriod: string,
    actualPeriod: string,
    currentPeriod: string,
    futurePeriod: string,
    customDateField: string,
    leaseType: string,
    query = '',
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };

    // Use util to build query string
    const finalQuery = buildReportQuery({
      period,
      startDate,
      endDate,
      startPeriod,
      endPeriod,
      scheduledPeriod,
      actualPeriod,
      currentPeriod,
      futurePeriod,
      date,
      customDateField,
      leaseType,
      query,
    });
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/csv-reports/${reportId}${finalQuery}`,
      options,
    );
  }

  downloadPDFReport(
    organizationId: string,
    propertyId: string,
    reportId: string,
    startDate: string,
    endDate: string,
    period: string,
    date: string,
    startPeriod: string,
    endPeriod: string,
    scheduledPeriod: string,
    actualPeriod: string,
    currentPeriod: string,
    futurePeriod: string,
    customDateField: string,
    leaseType: string,
    query = '',
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    // Use util to build query string
    const finalQuery = buildReportQuery({
      period,
      startDate,
      endDate,
      startPeriod,
      endPeriod,
      scheduledPeriod,
      actualPeriod,
      currentPeriod,
      futurePeriod,
      date,
      customDateField,
      leaseType,
      query,
    });
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/pdf-reports/${reportId}${finalQuery}`,
      options,
    );
  }
  download(organizationId: string, reportId: string, propertyId: string) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
      },
    };

    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/reports/${reportId}/download-last-created`,
      options,
    );
  }

  downloadAffordableStateReport(
    organizationId: string,
    propertyId: string,
    id: ?string,
    startDate: string,
    endDate: string,
    period: ?string,
    date: ?string,
    startPeriod: string,
    endPeriod: string,
    scheduledPeriod: string,
    actualPeriod: string,
    currentPeriod: string,
    futurePeriod: string,
    customDateField: string,
    leaseType: string,
    query = '',
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    const startDateQuery = date ? date : startDate;
    const finalQuery = `?startDate=${startDateQuery}&endDate=${endDate}${query}`;
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/affordable-state-reports${finalQuery}`,
      options,
    );
  }

  downloadUnitDataSheetReport(
    organizationId: string,
    propertyId: string,
    id: ?string,
    startDate: string,
    endDate: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    const finalQuery = `?startDate=${startDate}&endDate=${endDate}`;
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/download-unit-data-sheet-report${finalQuery}`,
      options,
    );
  }

  downloadMINCTenantTransactionsReport(
    organizationId: string,
    propertyId: string,
    id: ?string,
    startDate: string,
    endDate: string,
    period: ?string,
    date: ?string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    const finalQuery = `?startDate=${startDate}&endDate=${endDate}`;
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/minc-tenant-transactions${finalQuery}`,
      options,
    );
  }

  downloadScheduleAReport(
    organizationId: string,
    propertyId: string,
    reportId: string,
    startDate: string,
    endDate: string,
    period: string,
    date: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/schedule-A-report?date=${date}`,
      options,
    );
  }

  uploadReport(
    organizationId: string,
    propertyId: string,
    file: any,
    reportId: string,
  ) {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
      },
      body: JSON.stringify(file),
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/reports/${reportId}`,
      options,
    );
  }
  downloadPreviousReport(
    organizationId: string,
    reportId: string,
    propertyId: string,
    reportHistoryId: string,
  ) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
      },
    };

    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/reports/${reportId}/download-previous/${reportHistoryId}`,
      options,
    );
  }

  downloadZipFileForMonthEndClose(
    organizationId: string,
    propertyId: string,
    fileType: string,
    period: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };

    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/ame-reports?fileType=${fileType}&period=${period}`,
      options,
    );
  }

  downloadComplianceOverviewReport(
    organizationId: string,
    complianceType: string,
    includeNotStartedCerts: boolean,
    fileType: string,
    searchText?: string,
    filters?: Object,
    sorting?: Object,
  ) {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
      },
    };

    const includeNotStarted = includeNotStartedCerts
      ? '&includeNotStartedCerts=true'
      : '';
    const queryString = new URLSearchParams({
      ...(!isEmpty(searchText ?? '') && { searchText }),
      ...(!isEmpty(filters) && { filter: JSON.stringify(filters) }),
      ...(!isEmpty(sorting) && {
        sort_by: sorting.fieldName,
        order_by: sorting.order,
      }),
      timeZone,
    }).toString();

    return requestToDownload(
      // eslint-disable-next-line max-len
      `${apiURL}/${organizationId}/compliance-overview/report/${fileType}?complianceType=${complianceType}${includeNotStarted}${
        !isEmpty(queryString) ? `&${queryString}` : ''
      }`,
      options,
    );
  }
}
